/**
 * When a Reporting chart has enough history to draw.
 *
 * A chart is worth showing only once its series holds MORE than
 * `SPARSE_VALUE_LIMIT` values. Three points or fewer read as noise, not a
 * trend, and a mostly empty chart box says less than no chart at all.
 *
 * What counts as a value: a distinct x with a defined, finite y. A zero is a
 * value (an observed zero balance is history). A missing, null, or NaN y is
 * not, and a repeated x counts once.
 *
 * Two places apply the rule:
 *   - Panel visibility is judged against the chart's FULL history, so a
 *     panel never disappears just because the reader picked a short range.
 *   - Inside the selected range, a sparse window keeps the panel and shows a
 *     compact note in place of the plot.
 */

export const SPARSE_VALUE_LIMIT = 3;

export interface ChartValue {
    x: number;
    y: number | null | undefined;
}

export function countChartValues(values: readonly ChartValue[]): number {
    const xs = new Set<number>();
    for (const { x, y } of values) {
        if (!Number.isFinite(x)) continue;
        if (typeof y !== "number" || !Number.isFinite(y)) continue;
        xs.add(x);
    }
    return xs.size;
}

/** True when the series has more than `SPARSE_VALUE_LIMIT` values. */
export function hasChartHistory(values: readonly ChartValue[]): boolean {
    return countChartValues(values) > SPARSE_VALUE_LIMIT;
}

/* ── Range ──────────────────────────────────────────────── */

export type ReportingRange = "7d" | "30d" | "90d" | "1y" | "all";

export const REPORTING_RANGES: ReadonlyArray<{ label: string; value: ReportingRange }> = [
    { label: "7d", value: "7d" },
    { label: "30d", value: "30d" },
    { label: "90d", value: "90d" },
    { label: "1y", value: "1y" },
    { label: "All", value: "all" }
];

const RANGE_DAYS: Record<ReportingRange, number | null> = {
    "7d": 7,
    "30d": 30,
    "90d": 90,
    "1y": 365,
    all: null
};

export const DAY_MS = 24 * 60 * 60 * 1000;

export function isReportingRange(value: string): value is ReportingRange {
    return value in RANGE_DAYS;
}

/**
 * The rows inside a range, measured back from the series' latest point (not
 * from today, so a snapshot that lags a day still fills its window). A
 * window is calendar time, not a row count: a weekly series in a 7d window
 * holds one or two rows, which is what makes the in-range note reachable.
 */
export function windowByRange<T extends { t: number }>(rows: readonly T[], range: ReportingRange): T[] {
    const days = RANGE_DAYS[range];
    if (days === null || rows.length === 0) return rows.slice();
    let latest = -Infinity;
    for (const row of rows) latest = Math.max(latest, row.t);
    const start = latest - (days - 1) * DAY_MS;
    return rows.filter((row) => row.t >= start);
}
